// src/pages/PrivacyPolicyPage.tsx
import React from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiShield } from "react-icons/fi";
import { usePageTitle } from "../hooks/usePageTitle";

const PrivacyPolicyPage: React.FC = () => {
	usePageTitle("プライバシーポリシー");

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<div className="mb-8">
					<Link
						to="/"
						className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
					>
						<FiArrowLeft className="w-4 h-4 mr-1" />
						トップページに戻る
					</Link>
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
						<div className="flex items-center gap-4 mb-4">
							<div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full">
								<FiShield className="w-6 h-6 text-white" />
							</div>
							<div>
								<h1 className="text-3xl font-bold text-gray-900 dark:text-white">
									プライバシーポリシー
								</h1>
								<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
									最終更新日: 2025年11月21日
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Privacy Policy Content */}
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
					<div className="prose prose-gray dark:prose-invert max-w-none">
						<section className="mb-8">
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								合同会社blackadder（以下「当社」といいます）は、イベントチケット販売プラットフォーム「b+
								ticket」（以下「本サービス」といいます）において、ユーザーの個人情報の重要性を認識し、個人情報の保護に関する法律（以下「個人情報保護法」といいます）を遵守するとともに、以下のプライバシーポリシー（以下「本ポリシー」といいます）に従い、適切な取扱い及び保護に努めます。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第1条（個人情報の定義）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								本ポリシーにおいて「個人情報」とは、個人情報保護法第2条第1項により定義された個人情報、すなわち、生存する個人に関する情報であって、当該情報に含まれる氏名、生年月日、住所、電話番号、メールアドレス等の記述等により特定の個人を識別することができるもの（他の情報と容易に照合することができ、それにより特定の個人を識別することができることとなるものを含む）を指します。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第2条（個人情報の収集方法）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								当社は、ユーザーから以下の方法により個人情報を収集します。
							</p>
							<ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>アカウント登録時にユーザーが入力する情報</li>
								<li>チケット購入時にユーザーが入力する情報</li>
								<li>主催者登録時に主催者が入力する情報</li>
								<li>お問い合わせフォーム等からユーザーが送信する情報</li>
								<li>
									本サービスの利用状況やアクセス情報（Cookie、IPアドレス、閲覧履歴等）
								</li>
								<li>決済代行会社等の外部サービスから提供される決済関連情報</li>
							</ol>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第3条（収集する個人情報の項目）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								当社が収集する個人情報の項目は以下のとおりです。
							</p>

							<div className="mb-4">
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
									1. 一般ユーザーの個人情報
								</h3>
								<ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
									<li>氏名</li>
									<li>メールアドレス</li>
									<li>電話番号</li>
									<li>決済情報（クレジットカード情報等）</li>
									<li>購入履歴</li>
									<li>本サービスの利用履歴</li>
									<li>Cookie等により取得される利用環境情報</li>
								</ul>
							</div>

							<div className="mb-4">
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
									2. 主催者の個人情報
								</h3>
								<ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
									<li>主催者名（個人名、団体名、会社名等）</li>
									<li>代表者氏名</li>
									<li>メールアドレス</li>
									<li>電話番号</li>
									<li>住所</li>
									<li>銀行口座情報</li>
									<li>ウェブサイトURL</li>
									<li>SNSアカウント情報</li>
									<li>本サービスの利用履歴、イベント開催実績</li>
								</ul>
							</div>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第4条（個人情報の利用目的）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								当社は、収集した個人情報を以下の目的で利用します。
							</p>

							<div className="mb-4">
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
									1. 一般ユーザーの個人情報の利用目的
								</h3>
								<ol className="list-decimal list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
									<li>本サービスの提供、運営、維持及び改善のため</li>
									<li>チケットの販売・発行及び入場管理のため</li>
									<li>決済処理及び領収書発行のため</li>
									<li>購入チケットに関する情報提供及び問い合わせ対応のため</li>
									<li>主催者への購入者情報の提供のため</li>
									<li>
										本サービスに関する重要なお知らせ、メンテナンス情報等の通知のため
									</li>
									<li>
										キャンペーン、新機能、イベント情報等のご案内のため（ユーザーが希望する場合）
									</li>
									<li>本サービスの利用状況の分析及びマーケティングのため</li>
									<li>不正利用の防止及びセキュリティ対策のため</li>
									<li>利用規約違反への対応のため</li>
									<li>その他、上記利用目的に付随する目的のため</li>
								</ol>
							</div>

							<div className="mb-4">
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
									2. 主催者の個人情報の利用目的
								</h3>
								<ol className="list-decimal list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
									<li>本サービスの提供、運営、維持及び改善のため</li>
									<li>主催者の本人確認及び審査のため</li>
									<li>イベント情報の掲載及び管理のため</li>
									<li>チケット売上の管理及び入金処理のため</li>
									<li>購入者情報の提供及びイベント運営支援のため</li>
									<li>売上レポート及び統計情報の提供のため</li>
									<li>本サービスに関する重要なお知らせ及び問い合わせ対応のため</li>
									<li>不正利用の防止及び利用規約違反への対応のため</li>
									<li>その他、上記利用目的に付随する目的のため</li>
								</ol>
							</div>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第5条（個人情報の第三者提供）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、以下のいずれかに該当する場合を除き、ユーザーの同意なく個人情報を第三者に提供しません。
							</p>
							<ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>法令に基づく場合</li>
								<li>
									人の生命、身体または財産の保護のために必要がある場合であって、本人の同意を得ることが困難であるとき
								</li>
								<li>
									公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合であって、本人の同意を得ることが困難であるとき
								</li>
								<li>
									国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、本人の同意を得ることにより当該事務の遂行に支障を及ぼすおそれがあるとき
								</li>
							</ol>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3 mb-3">
								2.
								前項の定めにかかわらず、当社は以下の場合において個人情報を第三者に提供します。この場合、提供先は第三者には該当しません。
							</p>
							<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 my-4">
								<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
									<li>
										<strong>主催者への提供</strong>:
										ユーザーがチケットを購入した場合、当該イベントの主催者に対し、購入者の氏名、メールアドレス、電話番号、購入チケット情報を提供します。
									</li>
									<li>
										<strong>決済代行会社への提供</strong>:
										決済処理のため、決済代行会社に対し必要な決済情報を提供します。
									</li>
									<li>
										<strong>業務委託先への提供</strong>:
										本サービスの運営に必要な範囲で、当社が業務を委託する事業者に個人情報を提供することがあります。この場合、当社は適切な監督を行います。
									</li>
								</ul>
							</div>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第6条（Cookie等の利用）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、本サービスの利便性向上、利用状況の分析、広告配信等のため、Cookie及びこれに類する技術を使用することがあります。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2. Cookieにより収集される情報には、以下が含まれます。
							</p>
							<ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
								<li>ブラウザの種類及びバージョン</li>
								<li>使用しているデバイスの種類</li>
								<li>オペレーティングシステム</li>
								<li>参照元URL</li>
								<li>アクセス日時</li>
								<li>IPアドレス</li>
								<li>閲覧ページ及びクリック情報</li>
							</ul>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3 mb-3">
								3.
								ユーザーは、ブラウザの設定によりCookieの受け入れを拒否することができます。ただし、Cookieを無効にした場合、本サービスの一部機能が正常に動作しない可能性があります。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								4.
								当社は、Google
								Analyticsその他の第三者が提供するアクセス解析ツールを使用することがあります。これらのツールは、Cookieを使用してユーザーの本サービスの利用状況を収集・分析します。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第7条（個人情報の管理）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、個人情報の正確性を保ち、これを安全に管理するため、適切な技術的・組織的安全管理措置を講じます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								当社は、個人情報への不正アクセス、紛失、破壊、改ざん、漏洩等のリスクに対し、合理的な安全対策を実施します。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								3.
								当社は、個人情報を取り扱う従業員に対し、個人情報保護に関する教育及び監督を行います。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								4.
								当社は、個人情報の取扱いを外部に委託する場合、委託先に対し適切な監督を行います。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第8条（個人情報の開示・訂正・削除）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								ユーザーは、当社が保有する自己の個人情報について、個人情報保護法に基づき開示を請求することができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								ユーザーは、開示された個人情報の内容に誤りがある場合、当社に対し訂正または削除を請求することができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								3.
								ユーザーは、個人情報の利用停止または消去を請求することができます。ただし、法令に基づき保存が義務付けられている情報、または本サービスの提供に必須の情報については、請求に応じられない場合があります。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								4.
								上記の請求を行う場合は、本ポリシー第12条に定める窓口までご連絡ください。当社は、本人確認を行った上で、合理的な期間内に対応します。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								5. 開示請求については、当社所定の手数料をいただく場合があります。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第9条（個人情報の保存期間）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								当社は、個人情報を利用目的の達成に必要な期間、および法令により保存が義務付けられている期間保存します。具体的な保存期間は以下のとおりです。
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>
									アカウント情報: アカウント削除後、または最終利用日から2年間
								</li>
								<li>購入履歴・決済情報: 法令に基づき7年間</li>
								<li>主催者情報: 主催者登録解除後、または最終利用日から5年間</li>
								<li>アクセスログ: 取得日から1年間</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第10条（未成年者の個人情報）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								当社は、原則として18歳未満の方から個人情報を収集しません。18歳未満の方が本サービスを利用する場合は、保護者の同意を得た上でご利用ください。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第11条（プライバシーポリシーの変更）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、法令の変更、事業内容の変更等により、本ポリシーを変更することがあります。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								本ポリシーを変更する場合、変更後のポリシーの効力発生日の14日前までに、本サービス上または当社ウェブサイトに掲載する方法により通知します。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								3.
								変更後、ユーザーが本サービスを継続して利用した場合、変更後のポリシーに同意したものとみなします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第12条（お問い合わせ窓口）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								本ポリシーに関するお問い合わせ、個人情報の開示・訂正・削除等のご請求は、以下の窓口までご連絡ください。
							</p>
							<div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 my-4">
								<p className="text-gray-700 dark:text-gray-300 mb-2">
									<strong>合同会社blackadder 個人情報保護管理者</strong>
								</p>
								<p className="text-gray-700 dark:text-gray-300 mb-2">
									メールアドレス: privacy@blackadder.co.jp
								</p>
								<p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
									※お問い合わせへの回答には、お時間をいただく場合があります。
									<br />
									※本人確認のため、追加の情報提供をお願いする場合があります。
								</p>
							</div>
						</section>

						<div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
							<p className="text-sm text-gray-600 dark:text-gray-400 text-center">
								合同会社blackadder
							</p>
							<p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-1">
								制定日: 2025年11月21日
							</p>
						</div>
					</div>
				</div>

				{/* Back to Top Button */}
				<div className="mt-6 text-center">
					<Link
						to="/"
						className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
					>
						<FiArrowLeft className="w-4 h-4 mr-2" />
						トップページに戻る
					</Link>
				</div>
			</div>
		</div>
	);
};

export default PrivacyPolicyPage;
